import { Component, inject, OnInit, signal, Input, effect } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { IndexIoService } from '../../../services/index-io.service';
import { NgIf } from '@angular/common';
import { HistoricalChartResponseDto } from '../../../models/dto/historicalchartresponse-dto';
import { PerformanceChartData } from '../../../models/dto/performancechartdata-dto';
import { ValoDataItem } from '../../../models/dto/valodataitem-dto';
import { ProgressSpinner } from 'primeng/progressspinner';
import 'chartjs-adapter-date-fns';
import { DarkModeService } from '../../../services/dark-mode.service';

@Component({
  selector: 'bnpp-indx-index-performancechart',
  imports: [
    ChartModule,
    DropdownModule,
    FormsModule,
    NgIf,
    ProgressSpinner,
  ],
  templateUrl: './index-performance-chart.component.html',
  styleUrls: ['./index-performance-chart.component.scss'],
  standalone: true,
})
export class IndexPerformanceChartComponent implements OnInit {
  private indexInfoService = inject(IndexIoService);
  private darkModeService = inject(DarkModeService);
  @Input() mdsCode: string = '';
  startDate: string = '';
  endDate: string = new Date().toISOString().split('T')[0];
  public indexHistoricalChartSignal = signal<
    HistoricalChartResponseDto[] | null
  >(null);
  indexHeaderInfo = this.indexInfoService.indexHeaderInfo;
  indexSubProductInfo = this.indexInfoService.indexSubProductInfo;
  performanceChartData: PerformanceChartData = {
    labels: [],
    datasets: [],
  };
  chartColors = ['#4bc0c0', '#eaa127', '#ea4427'];

  performanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          title: function (context: any) {
            return new Date(context[0].parsed.x).toISOString().split('T')[0];
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time', // Use time scale
        time: {
          unit: 'month',
          displayFormats: {
            day: 'dd MMM yyyy', // Shows "01 Jan 2025"
            month: 'dd MMM yyyy', // Shows "Jan 2025"
            year: 'dd MMM yyyy', // Shows "2025"
          },
          // Ensure ticks are spaced every 2 months
          minUnit: 'month',
          min: new Date(),
          max: new Date(),
        },
        ticks: {
          maxTicksLimit: 10, // Maximum allowed (but `stepSize` controls actual count)
          source: 'data',
          autoSkipPadding: 20,
          padding: 10, // Minimum space between labels
          maxRotation: 45, // Rotate up to 45° if needed
          minRotation: 0, // Start at 0° (horizontal)
        },
        offset: true, // Prevents clipping
        title: { display: false },
      },
      y: {
        min: 900,
        title: {
          display: false,
        },
      },
    },
  };

  timeFrames = [
    { label: ' Since Start Date ', value: 'sinceStart' },
    { label: ' 10Y ', value: '10Y' },
    { label: ' 5Y', value: '5Y' },
    { label: ' 3Y', value: '3Y' },
    { label: ' 1Y', value: '1Y' },
    { label: ' 6M', value: '6M' },
    { label: ' 3M', value: '3M' },
    { label: ' 1M', value: '1M' },
  ];

  selectedTimeFrame = this.timeFrames[0];
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  chartOptions = signal<any>(null as any);

  constructor() {
    effect(() => {
      const isDarkMode = this.darkModeService.isDarkMode();
      const chartOptionCopy = JSON.parse(JSON.stringify(this.performanceChartOptions));
      const colorVal = isDarkMode ? '#ffffff' : '#5A5A5A';
      chartOptionCopy.scales.x.ticks["color"] = colorVal;
      chartOptionCopy.scales.x["grid"] = {
        display: true,
        color: isDarkMode ? '#ffffff80' : '#f3f4f6'
      }
      chartOptionCopy.scales.y["ticks"] = { color: colorVal };
      chartOptionCopy.scales.y["grid"] = {
        display: true,
        color: isDarkMode ? '#ffffff80' : '#f3f4f6'
      }
      this.chartOptions.set(chartOptionCopy);
    })
  }

  ngOnInit(): void {
    this.indexInfoService.getIndexHeaderInfo(this.mdsCode);
    this.indexInfoService.getSubProductInfo(this.mdsCode);
    this.populateHistoricalData();
  }

  populateHistoricalData(): void {
    this.indexHistoricalChartSignal.set(null);
    this.performanceChartData = { labels: [], datasets: [] };
    this.indexInfoService
      .getIndexHistoricalchartInfo(this.mdsCode, this.startDate, this.endDate)
      .subscribe((data: HistoricalChartResponseDto[]) => {
        if (Array.isArray(data)) {
          this.transformHistoricalData(data);
        }
      });
  }

  transformHistoricalData(data: HistoricalChartResponseDto[]) {
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const labelValue = item.ValoData?.map((item: ValoDataItem) => item.Date);
      const dataValue = item.ValoData?.map((item: ValoDataItem) => item.Value);
      const valStarDate = this.getFirstValoDate(item) ?? '';
      if (!this.startDate) {
        this.startDate = valStarDate;
      }
      //Logic to handle edge case like 31st dec2019 and 1 jan 2020 data point getting overlapped for yearly case.
      if (item.ValoData.length > 1) {
        const startDateTime = new Date(valStarDate);
        const timeUnit = this.getTimeUnit(
          startDateTime,
          new Date(this.endDate)
        );
        const diffMs = Math.abs(
          startDateTime.getTime() - new Date(item.ValoData[1].Date).getTime()
        );
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (timeUnit === 'year' && Math.abs(diffDays - 1) < 0.001) {
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          dataValue.unshift(null as any);
          labelValue.unshift(`${startDateTime.getFullYear()}-01-01`);
        }
      }
      this.indexHistoricalChartSignal.set(data);
      this.performanceChartData.datasets.push({
        pointRadius: 0,
        borderWidth: 1,
        data: dataValue,
        fill: false,
        borderColor: this.chartColors[i],
        tension: 0.4,
      });
      this.performanceChartData.labels = labelValue;
    }

    const chartOptionCopy = this.chartOptions();

    chartOptionCopy.scales.y.min = this.calculateDynamicYMin();
    const { min, max } = this.getDateRange();
    chartOptionCopy.scales.x.time = {
      ...chartOptionCopy.scales.x.time,
      min,
      max,
      unit: this.getTimeUnit(min, max),
    };
    this.chartOptions.set(chartOptionCopy);
  }

  private calculateDynamicYMin(): number {
    if (!this.performanceChartData?.datasets?.length) return 0;

    // Find the minimum value across all datasets
    const allValues = this.performanceChartData.datasets.flatMap(
      (dataset) => dataset.data?.map((val) => parseInt(val, 10)) as number[]
    );
    const minValue = Math.min(...allValues);

    // Add a 10% buffer below the min (optional)
    return minValue * 0.95;
  }

  private getDateRange(): { min: Date; max: Date } {
    if (!this.performanceChartData?.labels?.length) {
      return { min: new Date(), max: new Date() };
    }

    const dates = this.performanceChartData.labels.map(
      (label) => new Date(label)
    );
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

    // Add a small buffer (e.g., 5% of the range) to avoid cutting off data
    const range = maxDate.getTime() - minDate.getTime();
    const buffer = range * 0.1;

    return {
      min: new Date(minDate.getTime() - buffer),
      max: new Date(maxDate.getTime() + buffer),
    };
  }

  private getTimeUnit(minDate: Date, maxDate: Date): string {
    const rangeInDays = Math.floor(
      (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (rangeInDays <= 40) {
      return 'day'; // Less than 1 month → daily ticks
    } else if (rangeInDays <= 180) {
      return 'month'; // 1-6 months → monthly ticks
    } else if (rangeInDays <= 730) {
      return 'month'; // 6 months-2 years → monthly ticks
    } else {
      return 'year'; // More than 2 years → yearly ticks
    }
  }

  private getFirstValoDate(item: HistoricalChartResponseDto): string | null {
    const first = item.ValoData?.[0];
    return first?.Date ?? null;
  }

  getDateNYearsAgo(n: number, isMonths?: boolean): string {
    const currentDate = new Date();
    if (isMonths) {
      // Handle months (e.g., 0.5 = 6 months)
      const monthsToSubtract = Math.round(n * 12); // Convert to months
      currentDate.setMonth(currentDate.getMonth() - monthsToSubtract);
    } else {
      // Handle years (original logic)
      currentDate.setFullYear(currentDate.getFullYear() - n);
    }
    return currentDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }

  onTimeFrameChange(event: DropdownChangeEvent): void {
    console.log('Selected Time Frame:', event.value);
    this.selectedTimeFrame = event.value;
    switch (this.selectedTimeFrame.value) {
      case '10Y':
        this.startDate = this.getDateNYearsAgo(10);
        break;
      case '5Y':
        this.startDate = this.getDateNYearsAgo(5);
        break;
      case '3Y':
        this.startDate = this.getDateNYearsAgo(3);
        break;
      case '1Y':
        this.startDate = this.getDateNYearsAgo(1);
        break;
      case '6M':
        this.startDate = this.getDateNYearsAgo(0.5, true);
        break;
      case '3M':
        this.startDate = this.getDateNYearsAgo(0.25, true);
        break;
      case '1M':
        this.startDate = this.getDateNYearsAgo(1 / 12, true);
        break;
      default:
        this.startDate = '';
    }
    this.populateHistoricalData();
  }
}
